import React, { useState } from 'react';
import youtubeData from '../data/youtube.json';
import { PlayCircle, Hourglass } from 'lucide-react';

const Youtube = () => {
  const { description, videos } = youtubeData;
  const proposedVideos = videos.filter(video => video.section !== 'overlay');
  const overlayVideo = videos.find(video => video.section === 'overlay');
  
  const [modalContent, setModalContent] = useState(null);

  const openModal = (type, content) => {
    setModalContent({ type, content });
  };

  const closeModal = () => {
    setModalContent(null);
  };

  const renderModal = () => {
    if (!modalContent) return null;

    return (
      <div
        onClick={closeModal}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#000',
            padding: '1rem',
            borderRadius: '8px',
            position: 'relative',
            maxWidth: '95vw',
            maxHeight: '95vh',
            width: '1200px',
            height: '675px',
          }}
        >
          <button
            onClick={closeModal}
            style={{
              position: 'absolute',
              top: '10px',
              right: '15px',
              fontSize: '2rem',
              background: 'transparent',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            ×
          </button>

          {modalContent.type === 'video' ? (
            <iframe
              width="1200"
              height="675"
              src={modalContent.content.replace('watch?v=', 'embed/')}
              title="YouTube Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ width: '100%', height: '100%', maxWidth: '1200px', maxHeight: '90vh' }}
            />
          ) : (
            <img
              src={modalContent.content}
              alt="Full Preview"
              style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: '6px' }}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '2rem', fontFamily: 'sans-serif' }}>
      <h2 style={{ marginBottom: '0.25rem' }}>YouTube Video Series Plan</h2>
      <p style={{ fontSize: '1rem', color: '#555', marginBottom: '1.5rem', maxWidth: '900px' }}>
        {description}
      </p>

      {/* Proposed Videos */}
      {proposedVideos.map((video) => (
        <div
          key={video.id}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '2rem',
            marginTop: '2rem',
            marginBottom: '3rem',
            border: '1px solid #ddd',
            borderRadius: '10px',
            padding: '1.5rem',
            backgroundColor: '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          }}
        >
          {/* Left: Thumbnail */}
          <div style={{ flex: '1 1 320px', maxWidth: '400px' }}>
            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#666', marginBottom: '0.5rem' }}>
              Video Asset
            </div>
            {video.youtubeUrl ? (
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  cursor: 'pointer',
                }}
                onClick={() => openModal('video', video.youtubeUrl)}
              >
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  style={{ width: '100%', borderRadius: '8px' }}
                />
                <PlayCircle
                  size={48}
                  color="#fff"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                    filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.6))',
                  }}
                />
              </div>
            ) : (
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                }}
              >
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  style={{ width: '100%', borderRadius: '8px' }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '1rem',
                  }}
                >
                  <Hourglass size={36} />
                  In Progress
                </div>
              </div>
            )}
            <h3 style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
              {video.title}
            </h3>
            <p style={{ fontSize: '0.85rem', color: '#444' }}>
              Runtime: {video.length} — Status: {video.status}
            </p>
          </div>

          {/* Right: Strategy */}
          <div style={{ flex: '2 1 400px', minWidth: '300px' }}>
            <h3 style={{ marginBottom: '0rem', fontSize: '1.8rem' }}>{video.title}</h3>
            <p style={{ whiteSpace: 'pre-line', marginBottom: '1rem', color: '#555', fontSize: '1.1rem', padding: "0rem 0rem 3rem 0rem" }}>{video.description}</p>

            <div style={{ fontSize: '0.85rem', color: '#333' }}>
              <p><strong>Purpose:</strong> {video.strategy.purpose}</p>
              <p><strong>Audience:</strong> {video.strategy.audience}</p>
              <p><strong>Key Takeaways:</strong></p>
              <ul style={{ paddingLeft: '1.2rem' }}>
                {video.strategy.takeaways.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
              <p><strong>CTA:</strong> {video.strategy.cta}</p>
            </div>
          </div>
        </div>
      ))}

      {/* Overlay Video Section */}
      {overlayVideo && (
        <div
          style={{
            border: '2px solid #EA7400',
            borderRadius: '12px',
            padding: '2rem',
            backgroundColor: '#fff',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          <h2 style={{ marginBottom: '1rem' }}>🎬 Video Overlay Anchor</h2>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '2rem',
            }}
          >
            <div style={{ flex: '1 1 320px', maxWidth: '400px' }}>
              {overlayVideo.youtubeUrl ? (
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                  onClick={() => openModal('video', overlayVideo.youtubeUrl)}
                >
                  <img
                    src={overlayVideo.thumbnail}
                    alt={overlayVideo.title}
                    style={{ width: '100%', borderRadius: '8px' }}
                  />
                  <PlayCircle
                    size={48}
                    color="#fff"
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'none',
                      filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.6))',
                    }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    borderRadius: '8px',
                  }}
                >
                  <img
                    src={overlayVideo.thumbnail}
                    alt={overlayVideo.title}
                    style={{ width: '100%', borderRadius: '8px' }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0,0,0,0.6)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '1rem',
                    }}
                  >
                    <Hourglass size={36} />
                    In Progress
                  </div>
                </div>
              )}
            </div>

            <div style={{ flex: '2 1 400px' }}>
              <h3 style={{ marginTop: '0rem', fontSize: '1.8rem' }}>{overlayVideo.title}</h3>
              <p style={{ whiteSpace: 'pre-line', marginTop: '0rem', color: '#555', fontSize: '1.1rem', padding: "0rem 0rem 2rem 0rem" }}>{overlayVideo.description}</p>
              <div style={{ fontSize: '0.85rem', color: '#333' }}>
                <p><strong>Runtime:</strong> {overlayVideo.length}</p>
                <p><strong>Status:</strong> {overlayVideo.status}</p>
                <p><strong>Purpose:</strong> {overlayVideo.strategy.purpose}</p>
                <p><strong>Audience:</strong> {overlayVideo.strategy.audience}</p>
                <p><strong>Key Takeaways:</strong></p>
                <ul style={{ paddingLeft: '1.2rem' }}>
                  {overlayVideo.strategy.takeaways.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
                <p><strong>CTA:</strong> {overlayVideo.strategy.cta}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {renderModal()}
    </div>
  );
};

export default Youtube;